import type { JSX } from 'react';
import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import * as Date from 'dg-web-shared/lib/Date.ts';
import * as NumberFormatter from 'dg-web-shared/lib/NumberFormatter.ts';
import {
    ColAlign,
    ColumnWidth,
    DefaultTableRow,
    EmptyTableHeaderColumn,
    SortDirection,
    Table,
    TableBody,
    TableColumn,
    TableHeader,
    TableHeaderColumn,
} from '../../../ui/table/Table.tsx';
import { collectionListTexts } from '../../i18n/CollectionsTexts.ts';
import { ClickHandler } from 'dg-web-shared/ui/Clickable.tsx';
import * as Text from '../../../common/i18n/Text.ts';
import { Guide } from './Guide.tsx';
import * as CurrentOperatorLoginState from '../../../common/state/CurrentOperatorLoginState.ts';
import * as Icons16 from 'dg-web-shared/ui/icons/Icons16.tsx';
import * as Icons24 from 'dg-web-shared/ui/icons/Icons24.tsx';
import { TomCollection } from 'dg-web-shared/model/TomCollection.ts';
import * as TomCollectionsState from '../../state/TomCollectionsState.ts';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { IconTopOffsetContainer } from '../../../zones/components/ZoneListBody.tsx';
import { ModalSpecialTabResultRefresher } from '../../../ui/layout/TabContent.tsx';
import { css } from '@emotion/css';
import { ColorHex } from '../../../ui/Colors.ts';
import { OperatorTypo } from '../../../ui/OperatorTypo.ts';
import { Icon16, Icon24 } from '../../../ui/icons/Icon.tsx';

export interface CollectionListTexts {
    headerDate: Text.Translation;
    headerParkingMeterName: Text.Translation;
    headerStick: Text.Translation;
    headerCollectionNumber: Text.Translation;
    chfAmount: Text.Translation;
    eurAmount: Text.Translation;
    tokenAmount: Text.Translation;
    headerTransactionCount: Text.Translation;

    tooManyRowsTitle: Text.Translation;
    tooManyRowsDescription: Text.Translation;

    noRowsTitle: Text.Translation;
    noRowsDescription: Text.Translation;
}

export function CollectionsList() {
    const { storeState, update } = useStore(store => ({
        login: CurrentOperatorLoginState.get(store),
        collections: TomCollectionsState.List.get(store),
        filter: TomCollectionsState.Filter.get(store),
    }));
    const { language } = useLanguage();

    function openCollectionDetail(collection: TomCollection): void {
        update(store =>
            TomCollectionsState.Detail.stateWrite(store, {
                selectedCollection: collection,
            }),
        );
    }

    function renderIntroduction(): JSX.Element {
        return <Guide login={storeState.login} language={language} />;
    }

    function renderTooManyRows(): JSX.Element {
        const texts = collectionListTexts[language];
        return (
            <ResultsError
                title={texts.tooManyRowsTitle()}
                description={texts.tooManyRowsDescription()}
            />
        );
    }

    function renderNoRows(): JSX.Element {
        const texts = collectionListTexts[language];
        return (
            <ResultsError
                title={texts.noRowsTitle()}
                description={texts.noRowsDescription()}
            />
        );
    }

    const texts = collectionListTexts[language];

    const collectionsListRefresher = (
        <ModalSpecialTabResultRefresher
            onRefreshClick={() =>
                update(TomCollectionsState.List.forceRefetch, true)
            }
            successRequest={
                storeState.collections.statusCode.statusCode === 200
            }
            pendingRequest={storeState.collections.pending}
        />
    );

    if (storeState.collections.pending) {
        return collectionsListRefresher;
    }

    if (!TomCollectionsState.Filter.filterActive(storeState.filter)) {
        return renderIntroduction();
    }

    if (
        storeState.collections.data.size > 0 &&
        storeState.collections.data.data.length < 1
    ) {
        return (
            <>
                {collectionsListRefresher} {renderTooManyRows()}
            </>
        );
    }

    if (storeState.collections.data.size < 1) {
        return (
            <>
                {collectionsListRefresher} {renderNoRows()}
            </>
        );
    }

    return (
        <>
            {collectionsListRefresher}
            <div>
                <Table>
                    <TableHeader>
                        <EmptyTableHeaderColumn
                            width={ColumnWidth._24px}
                            delimiterBottom={false}
                        />
                        <TableHeaderColumn
                            name={texts.headerCollectionNumber()}
                            width={ColumnWidth._72px}
                            delimiterBottom={false}
                        />
                        <TableHeaderColumn
                            name={texts.headerDate()}
                            selected={true}
                            direction={SortDirection.Descending}
                            width={ColumnWidth._136px}
                            delimiterBottom={false}
                        />
                        <TableHeaderColumn
                            name={texts.headerStick()}
                            width={ColumnWidth._136px}
                            delimiterBottom={false}
                        />
                        <TableHeaderColumn
                            name={texts.headerParkingMeterName()}
                            width={ColumnWidth.variable}
                            delimiterBottom={false}
                        />
                        <TableHeaderColumn
                            name={texts.headerTransactionCount()}
                            width={ColumnWidth._136px}
                            delimiterBottom={false}
                        />
                        <TableHeaderColumn
                            name={texts.chfAmount()}
                            width={ColumnWidth._108px}
                            align={ColAlign.right}
                            delimiterBottom={false}
                        />
                        <TableHeaderColumn
                            name={texts.eurAmount()}
                            width={ColumnWidth._108px}
                            align={ColAlign.right}
                            delimiterBottom={false}
                        />
                        <TableHeaderColumn
                            name={texts.tokenAmount()}
                            width={ColumnWidth._108px}
                            align={ColAlign.right}
                            delimiterBottom={false}
                        />
                        <EmptyTableHeaderColumn
                            width={ColumnWidth._24px}
                            delimiterBottom={false}
                        />
                    </TableHeader>
                    <TableBody>
                        {storeState.collections.data.data.map(
                            (c: TomCollection) => {
                                return (
                                    <CollectionRow
                                        collection={c}
                                        key={c.collectionId}
                                        language={language}
                                        onClick={() => openCollectionDetail(c)}
                                    />
                                );
                            },
                        )}
                    </TableBody>
                </Table>
            </div>
        </>
    );
}

interface ResultsErrorProps {
    title: string;
    description: string;
}

const ResultsError = (p: ResultsErrorProps) => (
    <div
        className={css({
            textAlign: 'center',
            height: '80px',
            position: 'absolute',
            left: 0,
            right: 0,
            top: '50%',
            marginTop: '-40px',
        })}
    >
        <div className={css({ color: ColorHex.error })}>
            <Icon24 icon={Icons24.error} />
        </div>
        <div
            className={css({
                color: ColorHex.error,
                ...OperatorTypo.headingTwo,
            })}
        >
            {p.title}
        </div>
        <div
            className={css({
                color: ColorHex.typo,
                ...OperatorTypo.headingOne,
            })}
        >
            {p.description}
        </div>
    </div>
);

interface CollectionRowProps {
    collection: TomCollection;
    language: string;
    onClick: ClickHandler;
}

const CollectionRow = (p: CollectionRowProps): JSX.Element => (
    <DefaultTableRow onClick={p.onClick}>
        <TableColumn width={ColumnWidth._24px} />
        <TableColumn width={ColumnWidth._72px}>
            {p.collection.collectionNumber}
        </TableColumn>
        <TableColumn width={ColumnWidth._136px}>
            {Date.Formatter.dayMonthYearHourMinute(p.collection.collectionTime)}
        </TableColumn>
        <TableColumn width={ColumnWidth._136px}>
            {p.collection.stickDescription}
        </TableColumn>
        <TableColumn width={ColumnWidth.variable}>
            {p.collection.tomExternalOperatorId
                ? p.collection.tomExternalOperatorId
                : p.collection.tomId}{' '}
            - {p.collection.tomName}
        </TableColumn>

        <TableColumn width={ColumnWidth._136px}>
            {p.collection.transactionCount}
        </TableColumn>
        <TableColumn width={ColumnWidth._108px} align={ColAlign.right}>
            {NumberFormatter.numberToPrice(
                p.collection.collectedAmountChf,
                false,
            )}
        </TableColumn>
        <TableColumn width={ColumnWidth._108px} align={ColAlign.right}>
            {NumberFormatter.numberToPrice(
                p.collection.collectedAmountEuro,
                false,
            )}
        </TableColumn>
        <TableColumn width={ColumnWidth._108px} align={ColAlign.right}>
            {NumberFormatter.numberToPrice(
                p.collection.collectedAmountToken,
                false,
            )}
        </TableColumn>
        <TableColumn width={ColumnWidth._24px}>
            <IconTopOffsetContainer>
                <Icon16 icon={Icons16.chevronRight} />
            </IconTopOffsetContainer>
        </TableColumn>
    </DefaultTableRow>
);
